import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatDateLong } from '../../utils/dateUtils';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { Business as BusinessIcon, Add as AddIcon } from '@mui/icons-material';

const getStatusColor = (status) => {
  switch (status) {
    case 'new':
      return 'primary';
    case 'contacted':
      return 'info';
    case 'quoted':
      return 'warning';
    case 'converted':
      return 'success';
    case 'declined':
      return 'error';
    default:
      return 'default';
  }
};

function EnquiryDetailDialog({ 
  open, 
  onClose, 
  enquiry, 
  onConvertToOrder,
  organizations = [],
  onCreateOrganization,
  onOrganizationChange
}) {
  const [selectedOrgId, setSelectedOrgId] = useState(enquiry?.organizationId || '');

  // Update selectedOrgId when enquiry changes
  React.useEffect(() => {
    setSelectedOrgId(enquiry?.organizationId || '');
  }, [enquiry]);

  if (!enquiry) return null;

  const handleOrganizationChange = (event) => {
    const orgId = event.target.value;
    setSelectedOrgId(orgId);
    if (onOrganizationChange) {
      onOrganizationChange(enquiry.id, orgId);
    }
  };

  const handleCreateOrganization = () => {
    if (onCreateOrganization) {
      onCreateOrganization(enquiry);
    }
  };

  const hasOrganization = !!selectedOrgId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Enquiry Details - #{enquiry.id}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Organisation Selection Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Organisation
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <FormControl fullWidth>
                  <InputLabel>Select Organisation</InputLabel>
                  <Select
                    value={selectedOrgId}
                    onChange={handleOrganizationChange}
                    label="Select Organisation"
                    disabled={!organizations || organizations.length === 0}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleCreateOrganization}
                  disabled={hasOrganization}
                  sx={{ minWidth: '200px', height: '56px' }}
                >
                  Create Organisation
                </Button>
              </Box>
              {hasOrganization && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Organisation selected. You can now convert this enquiry to an order.
                </Alert>
              )}
              {!hasOrganization && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please select or create an organization before converting to order.
                </Alert>
              )}
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">
                {enquiry.firstName} {enquiry.lastName}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Organisation
              </Typography>
              <Typography variant="body1">
                {enquiry.schoolOrBusinessName}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">
                {enquiry.emailAddress}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Phone
              </Typography>
              <Typography variant="body1">
                {enquiry.phoneNumber}
              </Typography>
            </Grid>

            {/* Enquiry Details */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Enquiry Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Product Description
              </Typography>
              <Typography variant="body1">
                {enquiry.productDescription}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Message
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {enquiry.message}
              </Typography>
            </Grid>

            {enquiry.howDidYouHear && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  How Did They Hear About Us
                </Typography>
                <Typography variant="body1">
                  {enquiry.howDidYouHear}
                </Typography>
              </Grid>
            )}

            {/* Status and Dates */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Status & Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={enquiry.status}
                color={getStatusColor(enquiry.status)}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Submitted
              </Typography>
              <Typography variant="body1">
                {enquiry.createdAt ? formatDateLong(enquiry.createdAt) : 'N/A'}
              </Typography>
            </Grid>

            {enquiry.notes && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Internal Notes
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {enquiry.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {enquiry.status !== 'converted' && onConvertToOrder && (
          <Button
            onClick={() => {
              onConvertToOrder(enquiry);
              onClose();
            }}
            variant="contained"
            color="success"
            disabled={!hasOrganization}
          >
            Convert to Order
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

EnquiryDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  enquiry: PropTypes.object,
  onConvertToOrder: PropTypes.func,
  organizations: PropTypes.array,
  onCreateOrganization: PropTypes.func,
  onOrganizationChange: PropTypes.func
};

export default EnquiryDetailDialog;
